import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import {
  DocumentVaultDeleteData,
  DocumentVaultItemModel,
  DocumentVaultSearchData,
  DocumentVaultUpdateProfileLabelsData,
  DocumentVaultUploadData,
} from '../../models/document.vault.item.model';
import { revertAll, RootState } from '../../store';
import { AIDocumentContractDeleteData } from '../../models/ai.document.contract.model';

export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async ({ data }: DocumentVaultSearchData) => {
    const response: any = await NWClient.list('vault-documents', data);
    return {
      results: response.results as DocumentVaultItemModel[],
      count: response.count,
      next: response.next,
      previous: response.previous,
    };
  }
);

export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async ({ data }: DocumentVaultUploadData) => {
    const response = await NWClient.post('document-upload/bulk_upload', data, true);
    return response;
  }
);

export const updateDocumentProfileItems = createAsyncThunk(
  'documents/updateDocumentProfileItems',
  async ({ id, data }: DocumentVaultUpdateProfileLabelsData) => {
    const response = await NWClient.patch(`document-upload/${id}/`, null, data);
    return response;
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async ({ id, userId }: DocumentVaultDeleteData) => {
    const response = await NWClient.delete('document-upload', id, null, { user: userId });
    return response;
  }
);

export const deleteDocumentContract = createAsyncThunk(
  'documentContracts/deleteDocumentContract',
  async ({ id, userId }: AIDocumentContractDeleteData) => {
    const response = await NWClient.delete('document-contract', id, null, { user: userId });
    return response;
  }
);

export interface DocumentsState {
  documentsList: DocumentVaultItemModel[];
  documentsCount: number;
  documentsPage: number;
  status: string;
  error: string;
  /**
   * The requestId of the newest fetchDocuments call. Only its answer is applied, so a slow
   * response to an earlier call (e.g. the Documents tab's first load arriving after the user
   * has switched to Subscriptions) can no longer overwrite the list being shown.
   */
  latestFetchRequestId: string | null;
}

const documentsArray: DocumentVaultItemModel[] = [];
const initialState: DocumentsState = {
  documentsList: documentsArray,
  documentsCount: 0,
  documentsPage: 1,
  status: 'idle',
  error: '',
  latestFetchRequestId: null,
};
const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocumentsPage(state, action: PayloadAction<number>) {
      state.documentsPage = action.payload;
    },
    clearDocuments() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDocuments.pending, (state, action) => {
        state.latestFetchRequestId = action.meta.requestId;
        if (!action.meta.arg.silent) {
          state.status = 'loading';
        }
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        if (!action.meta.arg.silent) {
          state.status = 'succeeded';
        }
        if (Array.isArray(action.payload.results)) {
          state.documentsList = [...action.payload.results];
          state.documentsCount = action.payload.count || 0;
        }
        if (action.payload.next) {
          const parsedURL = new URL(action.payload.next);
          const page = parsedURL.searchParams.get('page');
          state.documentsPage = Number(page) - 1;
        } else if (action.payload.previous) {
          const parsedURL = new URL(action.payload.previous);
          const page = parsedURL.searchParams.get('page');
          state.documentsPage = page ? Number(page) + 1 : 2;
        }
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        if (action.meta.arg.silent || action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        state.status = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(uploadDocument.pending, (state) => {
        state.status = 'uploading';
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.status = 'uploaded';
        state.documentsList.unshift(action.payload as DocumentVaultItemModel);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(updateDocumentProfileItems.pending, (state) => {
        state.status = 'updating';
      })
      .addCase(updateDocumentProfileItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.documentsList.findIndex((doc) => doc.id === action.meta.arg.id);
        state.documentsList[index] = { ...(action.payload as DocumentVaultItemModel) };
      })
      .addCase(updateDocumentProfileItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(deleteDocument.pending, (state) => {
        state.status = 'deleting';
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.documentsList = [...state.documentsList].filter((el) => el.id !== action.meta.arg.id);
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(deleteDocumentContract.pending, (state) => {
        state.status = 'deleteLoading';
      })
      .addCase(deleteDocumentContract.fulfilled, (state, action) => {
        state.status = 'deleteSucceeded';
        state.documentsList = [...state.documentsList].filter((el) => el.id !== action.meta.arg.id);
      })
      .addCase(deleteDocumentContract.rejected, (state, action) => {
        state.status = 'deleteFailed';
        state.error = action.error.message ?? '';
      })
      .addCase(revertAll, () => initialState);
  },
});

export default documentsSlice.reducer;
export const { clearDocuments, setDocumentsPage } = documentsSlice.actions;
export const selectAllDocuments = (state: RootState) => state.documents.documentsList;
export const selectDocumentsCount = (state: RootState) => state.documents.documentsCount;
export const selectDocumentsPage = (state: RootState) => state.documents.documentsPage;
